import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useProjectStore, { NO_PROJECT } from "../../../store/useProjectStore";
import { getStoredUser, hasUnrestrictedTargets } from "../../../utils/currentUser";

/**
 * The project picker, in the header, for every section that has one.
 *
 * Overview, Agent Library and Execution History each used to render their own
 * dropdown in the page body. They are the same control with different option
 * lists, so there is now one of them up here and the option list is chosen
 * from the route:
 *
 *   /overview          → the user's projects. A score history needs a project.
 *   /agents            → the user's projects, plus "No project" for the users
 *                        who are allowed to run without one (staff with free
 *                        URL entry, and guests, who own no project at all).
 *   /history           → "All Projects" (the default, as before) and
 *                        "No Project" on top of the user's projects, because
 *                        here it is a filter rather than a destination.
 *   /agent/:id         → same list as the library, so the project a run is
 *                        scoped to can be changed from inside the chat
 *                        instead of going back to the library for it.
 *
 * Everything else (settings, profile) has no project context, so nothing
 * renders.
 */

function sectionFor(pathname) {
  if (pathname.startsWith("/overview")) return "overview";
  if (pathname.startsWith("/agents") || pathname.startsWith("/agent-library"))
    return "library";
  if (pathname.startsWith("/history")) return "history";
  if (pathname.startsWith("/agent/")) return "chat";
  return null;
}

/** May this user run an agent with no project selected? */
function canRunWithoutProject() {
  return hasUnrestrictedTargets() || getStoredUser()?.role === "guest";
}

const FolderIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M1.5 3.5A1 1 0 012.5 2.5h4l1.5 2H13.5a1 1 0 011 1v7a1 1 0 01-1 1h-11a1 1 0 01-1-1v-9z"
      stroke="currentColor"
      strokeWidth="1.3"
      fill="none"
    />
  </svg>
);

export default function ProjectSwitcher() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const section = sectionFor(pathname);

  const projects = useProjectStore((s) => s.projects);
  const quota = useProjectStore((s) => s.quota);
  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);
  const historyProjectId = useProjectStore((s) => s.historyProjectId);
  const setSelectedProjectId = useProjectStore((s) => s.setSelectedProjectId);
  const setHistoryProjectId = useProjectStore((s) => s.setHistoryProjectId);

  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);

  const isHistory = section === "history";
  const allowNone = canRunWithoutProject();

  // Close on outside click, on Escape, and whenever the route changes — the
  // list it is showing belongs to the section that was on screen.
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const options = useMemo(() => {
    const real = projects.map((p) => ({
      id: p.id,
      label: p.name,
      domain: p.domain,
      competitors: p.competitors,
    }));
    if (isHistory) {
      return [
        { id: null, label: "All Projects" },
        { id: NO_PROJECT, label: "No Project" },
        ...real,
      ];
    }
    // A score history belongs to a project, so Overview never offers "none".
    if (section === "overview") return real;
    return allowNone ? [...real, { id: NO_PROJECT, label: "No project" }] : real;
  }, [projects, isHistory, section, allowNone]);

  // Keep the working project honest: a deleted project, or a first visit with
  // nothing stored, lands on the active project and then the first one. This
  // is the fallback each section used to run for itself.
  useEffect(() => {
    if (!section || isHistory) return;
    // No list to validate against yet — before the first fetch lands, and in
    // the window between Sign Out clearing the store and the app unmounting.
    // Clearing the selection here would only fight whatever still holds the
    // old list. An empty list that is genuinely empty is handled by Layout,
    // which calls clearProjects() when the API returns zero projects.
    if (!projects.length) return;
    const isReal =
      selectedProjectId && projects.some((p) => p.id === selectedProjectId);
    if (isReal) return;
    // "No project" is a deliberate choice, not a stale id — but Overview has
    // nothing to show for it, so there it falls through to a real project.
    if (
      selectedProjectId === NO_PROJECT &&
      allowNone &&
      section !== "overview"
    ) {
      return;
    }
    const fallback =
      projects.find((p) => p.isDefault)?.id ||
      projects[0]?.id ||
      (allowNone ? NO_PROJECT : null);
    if (fallback !== selectedProjectId) setSelectedProjectId(fallback);
  }, [
    section,
    isHistory,
    projects,
    selectedProjectId,
    allowNone,
    setSelectedProjectId,
  ]);

  if (!section) return null;
  // History's dropdown only ever appeared once the user had a project; and
  // with no project at all there is nothing to switch between anywhere.
  if (!projects.length) return null;
  if (!options.length) return null;

  const value = isHistory ? historyProjectId : selectedProjectId;
  const onSelect = isHistory ? setHistoryProjectId : setSelectedProjectId;

  const current =
    options.find((o) => o.id === value) ||
    (isHistory ? options[0] : null);
  const label = current ? current.label : "Select a project";

  // The "New Project (2/3)" footer the Agent Library's dropdown used to carry.
  // Not on history, where the control is a filter, and not inside a chat,
  // where leaving for Settings would abandon the run being configured.
  const canCreate = !isHistory && section !== "chat" && !!quota;

  // One option is not a choice — show what is in scope without a menu that
  // can only confirm it. (Overview did exactly this with a single project.)
  // Unless the menu still has something to offer: creating the next project.
  if (options.length === 1 && !(canCreate && quota.canCreate)) {
    return (
      <div className="hdr__project__switch">
        <span className="hdr__project__eyebrow">Project:</span>
        <span className="hdr__project__btn is__static selected" title={label}>
          <FolderIcon />
          <span className="hdr__project__label">{label}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="hdr__project__switch pos__rel" ref={ref}>
      {/* Without this the chip is just a name sitting in the header, with
          nothing saying what it governs. */}
      <span className="hdr__project__eyebrow">Project:</span>
      <button
        type="button"
        ref={triggerRef}
        className={`hdr__project__btn ${current ? "selected" : ""} ${open ? "active" : ""}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Project: ${label}`}
        onClick={() => setOpen((v) => !v)}
      >
        <FolderIcon />
        <span className="hdr__project__label">{label}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden="true"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s",
            flexShrink: 0,
          }}
        >
          <path
            d="M2 3.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="hdr__project__menu" role="listbox">
          <div className="hdr__project__list">
            {options.map((opt) => (
              <button
                type="button"
                role="option"
                aria-selected={value === opt.id}
                className={
                  value === opt.id
                    ? "hdr__project__item active"
                    : "hdr__project__item"
                }
                key={opt.id ?? "all"}
                onClick={() => {
                  onSelect(opt.id);
                  setOpen(false);
                }}
              >
                <span className="dots"></span>
                <span className="hdr__project__item__inner">
                  <span className="p__name fb__12 fw__500">{opt.label}</span>
                  {opt.domain &&
                    (opt.domain !== opt.label ||
                      opt.competitors?.length > 0) && (
                      <span className="p__domain__comp fb__11 fw__400">
                        {opt.domain}
                        {opt.competitors?.length
                          ? ` · +${opt.competitors.length} competitor${
                              opt.competitors.length > 1 ? "s" : ""
                            }`
                          : ""}
                      </span>
                    )}
                </span>
              </button>
            ))}
          </div>

          {canCreate &&
            (quota.canCreate ? (
              <button
                type="button"
                className="hdr__project__new"
                onClick={() => {
                  setOpen(false);
                  navigate("/settings?new=true");
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M6 1v10M1 6h10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
                New Project ({quota.used}/{quota.maxProjects})
              </button>
            ) : (
              <div className="hdr__project__limit fb__11 fc__5F636A">
                {`${quota.used} of ${quota.maxProjects} project${
                  quota.maxProjects > 1 ? "s" : ""
                } used — contact your administrator for more.`}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
